// lib/analytics.ts
// GA4 맞춤 이벤트 전송 헬퍼.
//
// 이 사이트는 GTM 없이 gtag.js 를 직접 심는다(app/layout.tsx, G-WH11C4L4DZ).
// 따라서 버튼 클릭 같은 행동 이벤트는 코드에서 직접 보내야 한다.
//
// page_view·scroll 같은 자동 이벤트만으로는 "페이지만 보고 나간 사용자"와 "실제로
// 기능을 쓴 사용자"를 구분할 수 없다. 그 비율을 보려고 아래 이벤트들을 심었다.
//
// 이벤트 이름은 GA4 규칙에 맞춘다 — snake_case, 40자 이내, 예약 접두어(google_,
// firebase_, ga_) 금지. 파라미터는 이름 40자·값 100자 제한이 있어 긴 문자열은 자른다.

declare global {
  interface Window {
    // gtag.js 가 layout.tsx 의 인라인 스크립트에서 정의한다.
    // afterInteractive 로드라 초기 렌더 시점에는 없을 수 있어 optional 이다.
    gtag?: (
      command: 'event' | 'config' | 'js' | 'set',
      targetOrName: string | Date,
      params?: Record<string, unknown>
    ) => void;
  }
}

/**
 * 프로젝트에서 쓰는 맞춤 이벤트 이름. 오타로 유령 이벤트가 생기지 않게 좁혀 둔다.
 *
 * 이름은 적게, 파라미터는 풍부하게 가는 원칙이다. 예를 들어 교정 반영·무시·삭제를
 * 각각 다른 이벤트로 두면 GA4 에서 "결과를 만진 사용자" 를 합산하기 번거로워진다.
 * `revision_action` 하나에 `action` 파라미터로 구분한다.
 */
export type AnalyticsEvent =
  /** 맞춤법/형태소 '검사하기'·'분석하기' 실행. tool 로 도구 구분 */
  | 'click_inspect'
  /** 결과 복사(본문 텍스트·JSON). format 으로 구분 */
  | 'click_copy'
  /** 결과 내려받기(JSON·SVG·PNG·엑셀). format 으로 구분 */
  | 'download_result'
  /** 교정 결과 조작 — 반영·무시·삭제·복원·대안 변경·이동·전체교체·실행취소 */
  | 'revision_action'
  /** 결과 보기 전환 — 형태소 태깅 / 형태소 테이블 / JSON */
  | 'view_result'
  /** 분석 옵션 토글 — 의미 구분(WSD)·원형·자동 띄어쓰기·문장 분리 등 */
  | 'toggle_option'
  /** 결과 화면에서 '새 검사'·'새 분석' 으로 되돌아가기 */
  | 'new_analysis'
  /** '지난 입력' 에서 이전 텍스트를 불러오기 */
  | 'use_recent_text'
  /** 상단 탭·헤더의 다른 기능/내부 링크 이동 */
  | 'click_other_menu'
  /**
   * 설치 파일 내려받기·docker pull 명령 복사. kind 로 구분한다. (#288)
   *
   * 서버(`/api/download`)에도 같은 클릭이 남는다. GA4 는 광고 차단기에 막히므로
   * 추이의 근거는 서버 기록을 쓰고, 이 이벤트는 유입 경로와 묶어 보는 용도다.
   */
  | 'click_download'
  /**
   * 전체 복사 직후의 별점 창 (#291). action 으로 단계를 구분한다 —
   * shown(창이 뜸)·score(별점 누름)·text(한 줄 평 제출)·report(잘못 고친 문장 제보)·dismiss(닫음).
   */
  | 'site_review';

type ParamValue = string | number | boolean | null | undefined;

/** GA4 파라미터 값 상한(문자열 100자). 초과분은 잘라 보낸다. */
const MAX_PARAM_LENGTH = 100;

function normalizeParams(
  params?: Record<string, ParamValue>
): Record<string, string | number | boolean> {
  if (!params) return {};

  return Object.entries(params).reduce<
    Record<string, string | number | boolean>
  >((acc, [key, value]) => {
    if (value === undefined || value === null) return acc;
    acc[key] =
      typeof value === 'string' ? value.slice(0, MAX_PARAM_LENGTH) : value;
    return acc;
  }, {});
}

/**
 * GA4 로 맞춤 이벤트를 보낸다.
 *
 * 분석은 부가 기능이므로 절대 기능을 깨뜨리지 않는다 — SSR(window 없음), 광고
 * 차단기나 스크립트 로드 실패로 gtag 가 없는 경우, 전송 중 예외까지 모두 조용히 넘긴다.
 */
export function trackEvent(
  name: AnalyticsEvent,
  params?: Record<string, ParamValue>
): void {
  try {
    if (typeof window === 'undefined') return;

    const gtag = window.gtag;
    if (typeof gtag !== 'function') return;

    gtag('event', name, normalizeParams(params));
  } catch {
    // 분석 실패가 사용자 동작을 막아선 안 된다.
  }
}
